import { ESdChangeReason, IComponent, IStylingPosition, InvisibleStylePart } from "@kuika/kuika-cl-model";
import { Tree } from "antd/es";
import type { DataNode } from "antd/es/tree";
import * as React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./styling-visibility-settings.scss";

export interface IStylingVisibilityModalState {
  treeData: DataNode[];
  isSubmitButtonLoading: boolean;
  autoExpandParent: boolean;
}

export interface IStylingVisibilityModalProps {
  showModal?: boolean;
  closeModal?: () => void;
  editingComponent?: IComponent;
  onCheck?: (checkedKeys: any, info: any) => void;
  checkedKeys?: string[];
}

class StylingVisibilityModal extends PureSdComponent<
  IStylingVisibilityModalProps & IWithSDProps,
  IStylingVisibilityModalState
> {
  constructor(props: IStylingVisibilityModalProps & IWithSDProps) {
    super(props);
    this.state = {
      treeData: [
        {
          title: "Layout",
          key: InvisibleStylePart.Layout,
          children: [
            { title: "Padding", key: InvisibleStylePart.Layout_Padding },
            { title: "Width", key: InvisibleStylePart.Layout_Width },
            { title: "Height", key: InvisibleStylePart.Layout_Height },
            { title: "Min Width", key: InvisibleStylePart.Layout_MinWidth },
            { title: "Min Height", key: InvisibleStylePart.Layout_MinHeight },
            { title: "Max Width", key: InvisibleStylePart.Layout_MaxWidth },
            { title: "Max Height", key: InvisibleStylePart.Layout_MaxHeight },
            { title: "Align Items", key: InvisibleStylePart.Layout_AlignItems },
            { title: "Text Align", key: InvisibleStylePart.Layout_TextAlign },
            { title: "Display", key: InvisibleStylePart.Layout_Display },
            { title: "X Axis", key: InvisibleStylePart.Layout_XAxis },
            { title: "Y Axis", key: InvisibleStylePart.Layout_YAxis }
          ]
        },
        {
          title: "Text",
          key: InvisibleStylePart.Text,
          children: [
            { title: "Font Family", key: InvisibleStylePart.Text_FontFamily },
            { title: "Font Weight", key: InvisibleStylePart.Text_FontWeight },
            { title: "Font Size", key: InvisibleStylePart.Text_FontSize },
            { title: "Color", key: InvisibleStylePart.Text_Color },
            { title: "Line Height", key: InvisibleStylePart.Text_LineHeight },
            { title: "Letter Spacing", key: InvisibleStylePart.Text_LetterSpacing },
            { title: "Text Decoration", key: InvisibleStylePart.Text_TextDecoration },
            { title: "Text Transform", key: InvisibleStylePart.Text_TextTransform },
            { title: "Dont Affected Mobile System", key: InvisibleStylePart.Text_DontAffectedMobileSystem }
          ]
        },
        {
          title: "Fill",
          key: InvisibleStylePart.Fill,
          children: [
            { title: "Color", key: InvisibleStylePart.Fill_Color },
            { title: "Background Image", key: InvisibleStylePart.Fill_BackgroundImage },
            { title: "Repeat Switch", key: InvisibleStylePart.Fill_RepeatSwitch },
            { title: "Background Image Size", key: InvisibleStylePart.Fill_BackgroundImageSize },
            { title: "Stretch", key: InvisibleStylePart.Fill_Stretch },
            { title: "Background Image Position", key: InvisibleStylePart.Fill_BackgroundImagePosition }
          ]
        },
        {
          title: "Border",
          key: InvisibleStylePart.Border,
          children: [
            { title: "Border Color", key: InvisibleStylePart.Border_Color },
            { title: "Border Width", key: InvisibleStylePart.Border_Width },
            { title: "Border Radius", key: InvisibleStylePart.Border_Radius },
            { title: "Border Style", key: InvisibleStylePart.Border_Style }
          ]
        },
        {
          title: "Shadow",
          key: InvisibleStylePart.Shadow,
          children: [
            { title: "Color", key: InvisibleStylePart.Shadow_Color },
            { title: "Structure", key: InvisibleStylePart.Shadow_Structure }
          ]
        },
        {
          title: "Effects",
          key: InvisibleStylePart.Effects
          // Section-level only — no sub-parts (master plan §12 decision).
          // Hiding "Effects" hides the entire Effects panel (opacity, cursor,
          // pointer-events, mix-blend-mode, outline). Per-property granularity
          // is not supported; if needed in the future, add `Effects_X` enum
          // values + children entries here.
        }
      ],
      isSubmitButtonLoading: false,
      autoExpandParent: true
    };
  }

  componentDidMount() {}

  getBackupCustomKeys = (backup: IStylingPosition | undefined) => {
    if (backup) {
      const backupKeys = Object.keys(backup);
      return backupKeys;
    }
  };

  onSubmit = async (data: any) => {
    try {
      this.setState({ isSubmitButtonLoading: true });
      this.setState({ isSubmitButtonLoading: false });
      if (this.props.closeModal) {
        this.props.closeModal();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.setState({ isSubmitButtonLoading: false });
    }
  };

  onFinishFailed = (errorInfo) => {};

  render() {
    const hiddenCount: number = this.props.checkedKeys?.length ?? 0;
    return (
      <KModal
        className="kuika-styling-visibility"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        header="Hide Styling"
        width={900}
        centered
        dontRenderSpinner
        footer={
          <KModalFooter
            submitLabel="Update"
            submitTestId="stylingVisibilityUpdateButton"
            onSubmit={() => this.onSubmit(undefined)}
            loading={this.state.isSubmitButtonLoading}
          >
            <KuikaSmallText className="kuika-styling-visibility__count">
              {hiddenCount === 0 ? "Nothing hidden" : `${hiddenCount} hidden`}
            </KuikaSmallText>
          </KModalFooter>
        }
      >
        <div className="kuika-styling-visibility__body" data-testid="stylingVisibilityModal">
          <KuikaSmallText className="kuika-styling-visibility__caption">
            Ticked parts are hidden from the styling panel for this component. Ticking a section hides all of it.
          </KuikaSmallText>
          {/* Still an antd Tree: what gets persisted is the set of keys the tree's
              own parent/child cascade produces, and `inVisibleStyles` readers test
              each key with `includes`. Reimplementing that cascade by hand would
              silently change which keys land in saved component definitions. */}
          <Tree
            checkable
            autoExpandParent={this.state.autoExpandParent}
            onCheck={this.props.onCheck}
            checkedKeys={this.props.checkedKeys}
            treeData={this.state.treeData}
          />
        </div>
      </KModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const stylingVisibilityModal = withSD<IStylingVisibilityModalProps>(StylingVisibilityModal);
export { stylingVisibilityModal as StylingVisibilityModal };
